"use client";

import { usePathname, useRouter } from "next/navigation";
import { startTransition, useCallback, useEffect, useMemo } from "react";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useTranslations } from "@/i18n/client";
import { cn } from "@/lib/utils";
export const ProductVariantSelector = ({
  variants,
  optimisticSearchParams,
  setOptimisticSearchParams,
  subscription
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const subT = useTranslations("Global.subscription");
  const groupedByVariantType = useMemo(() => {
    return Object.values(variants.flatMap(item => item.combinations).reduce((acc, combination) => {
      const {
        label,
        type
      } = combination.variantValue.variantType;
      const {
        id,
        value,
        position,
        colorValue
      } = combination.variantValue;
      if (!acc[label]) {
        acc[label] = {
          variantType: {
            id: combination.variantValue.variantType.id,
            label,
            type: type || "string"
          },
          variantValues: []
        };
      }

      // Avoid adding duplicate variantValues
      if (!acc[label].variantValues.some(v => v.id === id)) {
        acc[label].variantValues.push({
          id,
          value,
          position: position || 1,
          colorValue: colorValue || undefined
        });
      }
      return acc;
    }, {})).map(v_0 => ({
      ...v_0,
      variantValues: v_0.variantValues.toSorted((v1, v2) => v1.position - v2.position)
    }));
  }, [variants]);
  const selectVariant = useCallback(({
    group,
    variantValue,
    replace
  }) => {
    const newParams = {
      ...optimisticSearchParams,
      [group.variantType.label]: variantValue.value
    };
    startTransition(() => setOptimisticSearchParams(newParams));
    const path = pathname + "?" + new URLSearchParams(newParams).toString();
    if (replace) {
      router.replace(path, {
        scroll: false
      });
    } else {
      router.push(path, {
        scroll: false
      });
    }
  }, [optimisticSearchParams, pathname, setOptimisticSearchParams]);
  const selectSubscription = useCallback(({
    subscription: subscription_0
  }) => {
    const newParams_0 = {
      ...optimisticSearchParams,
      ...(subscription_0 && {
        subscription_: subscription_0
      })
    };
    startTransition(() => setOptimisticSearchParams(newParams_0));
    const newSearchParams = new URLSearchParams(newParams_0);
    if (!subscription_0) {
      newSearchParams.delete("subscription_");
    }
    const path_0 = pathname + "?" + newSearchParams.toString();
    router.push(path_0, {
      scroll: false
    });
  }, [optimisticSearchParams, pathname, setOptimisticSearchParams]);
  useEffect(() => {
    // automatically check variant if there's only one

    const groupsToAutoSelect = groupedByVariantType.filter(group_0 => group_0.variantValues.length <= 1);
    if (groupsToAutoSelect.length === 0) {
      return;
    }
    groupsToAutoSelect.map(group_1 => {
      const variantValue_0 = group_1?.variantValues[0];
      const isChecked = variantValue_0 && group_1?.variantType.label && optimisticSearchParams[group_1.variantType.label] === variantValue_0.value;
      if (isChecked) {
        return;
      }
      if (group_1 && variantValue_0) {
        selectVariant({
          group: group_1,
          variantValue: variantValue_0,
          replace: true
        });
      }
    });
  }, [groupedByVariantType, selectVariant, optimisticSearchParams]);
  return <>
			<ul role="list" className="grid gap-2" aria-labelledby="variant-label">
				{groupedByVariantType.map(group_2 => {
        return <li key={group_2.variantType.id} className="grid gap-1">
							<h4 className="font-semibold text-sm">{group_2.variantType.label}</h4>
							<ul className="flex flex-row gap-2 flex-wrap">
								{group_2.variantValues.map(variantValue_1 => {
              const isChecked_0 = optimisticSearchParams[group_2.variantType.label] === variantValue_1.value;
              const shouldBeDisabled = shouldDisableButton(variants, optimisticSearchParams, group_2.variantType.label, variantValue_1.value);
              return <li key={variantValue_1.id}>
											<label className={cn("flex cursor-pointer items-center justify-center transition-colors", group_2.variantType.type === "color" ? "p-1 rounded-full" : "gap-2 rounded-md border px-8 py-2 hover:bg-(--theme-primary-background-color)/80", isChecked_0 && group_2.variantType.type !== "color" && "border-black bg-(--theme-primary-background-color)/60 font-medium", isChecked_0 && group_2.variantType.type === "color" && "ring-2 ring-black ring-offset-2", shouldBeDisabled && "text-neutral-400 opacity-50", "has-focus-visible:ring-3")}>
												{group_2.variantType.type === "color" ? <div className="w-10 h-10 rounded-full border-2 border-white shadow-md" style={{
                    backgroundColor: variantValue_1.colorValue || "#000000",
                    boxShadow: "0 0 0 1px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.1)"
                  }} title={variantValue_1.value} /> : variantValue_1.value}
												<input type="radio" name={group_2.variantType.label} value={variantValue_1.value} checked={isChecked_0} className="sr-only" onChange={() => {
                    selectVariant({
                      group: group_2,
                      variantValue: variantValue_1,
                      replace: false
                    });
                  }} />
											</label>
										</li>;
            })}
							</ul>
						</li>;
      })}
			</ul>
			{subscription && <div className="grid gap-2 mt-4">
					<h4 className="font-semibold text-sm">Subscription</h4>
					<RadioGroup value={optimisticSearchParams.subscription_ ?? ""} onValueChange={value_0 => {
        selectSubscription({
          subscription: value_0
        });
      }}>
						<div className="flex items-center space-x-2 p-3 border rounded-lg">
							<RadioGroupItem value="" id="one-time" />
							<Label htmlFor="one-time" className="flex-1 cursor-pointer">
								<div className="flex justify-between items-center">
									<span>{subT("oneTimePurchase")}</span>
									{/* <span className="font-semibold">${basePrice.toFixed(2)}</span> */}
								</div>
							</Label>
						</div>
						<div className="flex items-center space-x-2 p-3 border rounded-lg">
							<RadioGroupItem value={subscription.id} id="subscription" />
							<Label htmlFor="subscription" className="flex-1 cursor-pointer">
								<div className="flex justify-between items-center w-full">
									<div>
										<div className="flex items-center gap-2">
											<span>{subT("subscribeAndSave")}</span>
											{/* <Badge variant="secondary" className="text-xs">
            {Math.round(subscriptionDiscount * 100)}% OFF
            </Badge> */}
										</div>
										<div className="text-sm text-muted-foreground">{subT("cancelAnytime")}</div>
									</div>
									<div className="text-right ml-auto">
										{/* <div className="font-semibold">${getPrice().toFixed(2)}</div> */}
										{/* <div className="text-sm text-muted-foreground line-through">${basePrice.toFixed(2)}</div> */}
										{subscription && <p className="text-sm">
												{subT("subscriptionEvery")}{" "}
												{subT(subscription.cadence, {
                    interval: subscription.interval
                  })}
											</p>}
									</div>
								</div>
							</Label>
						</div>
					</RadioGroup>
				</div>}
		</>;
};

// Generated with GPT-4o and the following prompt:
// - I have a button for each variant type and variant value (e.g. size S, M, L, color Red, Blue, Green)
// - When that button is clicked, the variantValue for this variantType is selected
// - product.variants is an array that consists of allowed combinations of variantValues
// - determine whether a button should be disabled if a combination doesn't exist on product.variants
// - it should only disable this current button but leave other buttons enabled, i.e. return different answers for different buttons
// - for example, if allowed combinations for Size and Color are: [Red:S, Red:M, Red:L, Blue:S, Blue:L, Green:S, Green:M, Green:L]
//   then selecting Blue should disable M and selecting M should disable Blue but all other buttons should remain enabled.
function shouldDisableButton(variants, searchParams, variantTypeLabel, variantValueValue) {
  const simulatedSelection = {
    ...searchParams,
    [variantTypeLabel]: variantValueValue
  };
  const isValid = variants.some(variant => variant.combinations.every(combination => {
    const label = combination.variantValue.variantType.label;
    const val = combination.variantValue.value;
    return simulatedSelection[label] === undefined || simulatedSelection[label] === val;
  }) && (variant.stock === null || variant.stock > 0));
  return !isValid;
}